import { normalizeWardIdForPayload } from "../../../Apis/complaintApi";
import axiosInstance from "../../../../Hooks/axoisInstance";
import { API_BASE_URL } from "../../../Services/baseApiUrl";

export const INSPECTION_REPORT_ENDPOINTS = {
  GET_ALL_DHALAO_INSPECTIONS: `${API_BASE_URL}Inspector/GetAllDhalaoInspections`,
  GET_ALL_COMPACTING_STATION_INSPECTIONS: `${API_BASE_URL}Inspector/GetAllCompactingStationInspections`,
  GET_ALL_AUTO_TRIPPER_INSPECTIONS: `${API_BASE_URL}Inspector/GetAllAutoTripperInspections`,
  GET_ALL_STREET_BIN_INSPECTIONS: `${API_BASE_URL}Inspector/GetAllStreetBinInspections`,
  GET_ALL_MOBILE_TRANSFER_STATION_INSPECTIONS: `${API_BASE_URL}Inspector/GetAllMobileTransferStationInspections`,
  GET_ALL_REFUSE_COLLECTOR_INSPECTIONS: `${API_BASE_URL}Inspector/GetAllRefuseCollectorInspections`,
  GET_HOOK_LOADER_INSPECTIONS: `${API_BASE_URL}Inspector/GetHookLoaderInspections`,
  GET_CIRCLES: `${API_BASE_URL}Inspector/GetCircles`,
};

const formatInspectionDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const mapDhalaoInspection = (item = {}) => ({
  ...item,
  locationId: item.locationId,
  assetCode: item.assetCode,
  assetType: item.assetTypeName,
  dateOfInspection: formatInspectionDate(item.inspectionDate),
  physicalCondition: item.physicalCondition,
  signboard: item.signboard,
  paint: item.paint,
  flooring: item.flooring,
  bins: item.bins,
  litteringOutsideDhalaoAndAnyOther: item.litteringOutside,
  washingDates: formatInspectionDate(item.washingDates),
});

const mapCompactingStationInspection = (item = {}) => ({
  ...item,
  locationId: item.locationId,
  assetCode: item.assetCode,
  assetType: item.assetTypeName,
  dateOfInspection: formatInspectionDate(item.inspectionDate),
  physicalCondition: item.physicalCondition,
  signboard: item.signboard,
  paint: item.paint,
  flooring: item.flooring,
  bins: item.bins,
  anyOther: item.anyOther,
  washingDates: formatInspectionDate(item.washingDates),
});

const formatGpsStatus = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return value ?? "";
};

const mapAutoTripperInspection = (item = {}) => ({
  ...item,
  vehicleRegistrationNo: item.vehicleRegistrationNo,
  assetCode: item.assetCode,
  assetType: item.assetTypeName,
  dateOfInspection: formatInspectionDate(item.inspectionDate),
  vehicleCondition: item.vehicleCondition,
  anyDamage: item.anyDamage,
  paintAndSignage: item.paintSignage,
  isGpsFunctionalOrNot: formatGpsStatus(item.isGpsFunctional),
  lastServiceDate: formatInspectionDate(item.lastServiceDate),
});

const mapStreetBinInspection = (item = {}) => ({
  ...item,
  locationId: item.locationId,
  assetCode: item.assetCode,
  assetType: item.assetTypeName,
  dateOfInspection: formatInspectionDate(item.inspectionDate),
  physicalCondition: item.physicalCondition,
  signboard: item.signboard,
  paint: item.paint,
  flooring: item.flooring,
  litteringAroundBins: item.litteringAroundBins,
  anyOther: item.anyOther,
  washingDates: formatInspectionDate(item.washingDates),
});

const mapMobileTransferStationInspection = (item = {}) => ({
  ...item,
  subInspections: item.subInspectionTypeName,
  assetCode: item.assetCode,
  assetType: item.assetTypeName,
  dateOfInspection: formatInspectionDate(item.inspectionDate),
  vehicleCondition: item.vehicleCondition,
  anyDamage: item.anyDamage,
  paintAndSignage: item.paintSignage,
  isGpsFunctioningOrNot: formatGpsStatus(item.isGpsFunctional),
  lastServiceDate: formatInspectionDate(item.lastServiceDate),
});

const withinDateRange = (inspectionDate, fromDate, toDate) => {
  if (!inspectionDate) return true;
  if (!fromDate && !toDate) return true;

  const inspection = new Date(inspectionDate);
  const from = fromDate ? new Date(fromDate) : null;
  const to = toDate ? new Date(toDate) : null;

  if (Number.isNaN(inspection.getTime())) return true;
  if (from && inspection < from) return false;
  if (to && inspection > to) return false;
  return true;
};

const applyStaticReportFilters = (rows = [], filters = {}) =>
  rows.filter((row) => {
    const wardMatches =
      !filters.wardId || String(row.wardId) === String(filters.wardId);
    const dateMatches = withinDateRange(
      row.dateOfInspection || row.inspectionDate,
      filters.fromDate,
      filters.toDate
    );

    return wardMatches && dateMatches;
  });

const fetchStaticPaginatedInspectionReport = async ({
  staticRows = [],
  filters = {},
  pageNumber = 1,
  pageSize = 10,
  mapItem,
}) => {
  const filteredRows = applyStaticReportFilters(staticRows, filters);
  const start = (pageNumber - 1) * pageSize;
  const pageRows = filteredRows.slice(start, start + pageSize);

  return {
    data: pageRows.map(mapItem),
    totalRecords: filteredRows.length,
    totalPages: Math.ceil(filteredRows.length / pageSize) || 0,
    pageNumber,
    pageSize,
  };
};
const fetchPaginatedInspectionReport = async ({
  endpoint,
  filters = {},
  pageNumber = 1,
  pageSize = 10,
  mapItem,
}) => {
  const params = { pageNumber, pageSize };

  const normalizedWardId = normalizeWardIdForPayload(filters.wardId);
  if (normalizedWardId) params.wardId = normalizedWardId;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;

  const response = await axiosInstance.get(endpoint, { params });
  const body = response?.data ?? {};
  const list = Array.isArray(body?.data) ? body.data : [];

  return {
    data: list.map(mapItem),
    totalRecords: Number(body?.totalRecords ?? 0),
    totalPages: Number(body?.totalPages ?? 0),
    pageNumber: Number(body?.pageNumber ?? pageNumber),
    pageSize: Number(body?.pageSize ?? pageSize),
  };
};

const normalizeListResponse = (response) => {
  const list = response?.data ?? response?.result ?? response?.items ?? [];
  return Array.isArray(list) ? list : Array.isArray(response) ? response : [];
};

export const getInspectionReportWards = async () => {
  const response = await axiosInstance.get(
    INSPECTION_REPORT_ENDPOINTS.GET_CIRCLES
  );
  return normalizeListResponse(response?.data);
};

export const getConditionsOfDhalaoReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint: INSPECTION_REPORT_ENDPOINTS.GET_ALL_DHALAO_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapDhalaoInspection,
  });

export const getConditionsOfFixedCompactingStationsReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint: INSPECTION_REPORT_ENDPOINTS.GET_ALL_COMPACTING_STATION_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapCompactingStationInspection,
  });

export const getConditionsOfStreetCornerBinsReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint: INSPECTION_REPORT_ENDPOINTS.GET_ALL_STREET_BIN_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapStreetBinInspection,
  });

export const getConditionsOfMobileTransferStationsReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint:
      INSPECTION_REPORT_ENDPOINTS.GET_ALL_MOBILE_TRANSFER_STATION_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapMobileTransferStationInspection,
  });

export const getConditionsOfAutoTippersReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint: INSPECTION_REPORT_ENDPOINTS.GET_ALL_AUTO_TRIPPER_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapAutoTripperInspection,
  });

export const getConditionsOfRefuseCollectorTransportVehiclesReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint: INSPECTION_REPORT_ENDPOINTS.GET_ALL_REFUSE_COLLECTOR_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapAutoTripperInspection,
  });

export const getConditionsOfHookLoaderVehiclesReportList = async ({
  wardId = "",
  fromDate = "",
  toDate = "",
  pageNumber = 1,
  pageSize = 10,
} = {}) =>
  fetchPaginatedInspectionReport({
    endpoint: INSPECTION_REPORT_ENDPOINTS.GET_HOOK_LOADER_INSPECTIONS,
    filters: { wardId, fromDate, toDate },
    pageNumber,
    pageSize,
    mapItem: mapAutoTripperInspection,
  });
